// Reservas con proveedores vista previa (navegador): las mismas reglas que el servidor, sobre el almacén de prueba.
// Las contraseñas usan SHA-256 con sal (solo para la demostración). Nunca se muestran las claves ni la sal.
import * as R from "@/lib/reservas-proveedor-reglas";
import { hashSha, iguales, nuevaSal } from "./clave-web";
import type { Fila } from "./convertir";
import { conFallo, estaBloqueado, mensajeBloqueo, sinIntentos, type Intentos } from "./intentos";
import { proveedoresDemoReserva, CLAVE_DEMO_RESERVAS, disponibilidadDemo } from "./reservas-demo";
import type { Almacen } from "./proveedor";
import { normalizarUsuario, validarClave, validarUsuario } from "./validar";

type A = Almacen;
const ahora = () => new Date().toISOString();
const iso = (v: unknown) => String(v ?? "").slice(0, 10);
const aleatorio = (largo: number) => { const alf = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; return Array.from(crypto.getRandomValues(new Uint8Array(largo)), (b) => alf[b % alf.length]).join(""); };
const primerNombre = (n: unknown) => String(n ?? "").trim().split(/\s+/)[0] ?? "";

function bit(a: A, tabla: string, registroId: string, accion: string, antes?: unknown, despues?: unknown, motivo?: string) {
  a.tabla("bitacora").unshift({ id: a.nuevoId(), creadoEn: ahora(), tabla, registroId, accion, motivo: motivo ?? null, antes: antes ?? null, despues: despues ?? null });
}

/** Al entrar a la vista previa, los proveedores de prueba existen (una sola vez por navegador). */
export async function asegurarDemoReservas(a: A) {
  const hoy = R.hoyMexico();
  // Las demos ya abiertas conservan sus datos; añade el nuevo lugar sin borrar sus pruebas.
  if (a.tabla("proveedoresReserva").length) {
    const cuentaId = "dem-demo-fogon";
    const lugarId = "dem-fogon-mirador";
    if (proveedor(a, cuentaId) && !proveedor(a, lugarId)) {
      a.tabla("proveedoresReserva").push({ id: lugarId, cuentaProveedorId: cuentaId, tipo: "restaurante", nombre: "Terraza Mirador del Pino", contacto: "Persona de prueba", telefono: "3300000001", correo: null, ciudad: "Mazamitla", usuario: null, claveHash: null, estadoCuenta: "activa", cupoMaximo: 18, precioPorLugarCentavos: 0, intentosFallidos: 0, bloqueadoHasta: null, creadoEn: ahora() });
      for (const d of disponibilidadDemo("restaurante", 18, hoy)) a.tabla("disponibilidadReserva").push({ id: a.nuevoId(), proveedorId: lugarId, fecha: d.fecha, hora: d.hora, lugares: d.lugares, usados: 0 });
    }
    for (const cabana of a.tabla("cabanas")) if (!cabana.proveedorReservaId) cabana.proveedorReservaId = cuentaId;
    a.guardar();
    return;
  }
  for (const p of proveedoresDemoReserva) {
    const id = `dem-${p.usuario}`;
    const sal = nuevaSal();
    a.tabla("proveedoresReserva").push({ id, tipo: p.tipo, nombre: p.nombre, contacto: p.contacto, telefono: p.telefono, correo: null, ciudad: p.ciudad, usuario: p.usuario, sal, claveHash: await hashSha(CLAVE_DEMO_RESERVAS, sal), estadoCuenta: p.estadoCuenta, cupoMaximo: p.cupoMaximo, precioPorLugarCentavos: p.precioPorLugarCentavos, intentosFallidos: 0, bloqueadoHasta: null, creadoEn: ahora() });
    if (p.estadoCuenta === "activa") for (const d of disponibilidadDemo(p.tipo, p.cupoMaximo, hoy)) a.tabla("disponibilidadReserva").push({ id: a.nuevoId(), proveedorId: id, fecha: d.fecha, hora: d.hora, lugares: d.lugares, usados: 0 });
  }
  // Una misma cuenta de prueba administra dos lugares. Así se prueba el selector sin crear otro usuario.
  const cuentaId = "dem-demo-fogon";
  const lugarId = "dem-fogon-mirador";
  a.tabla("proveedoresReserva").push({ id: lugarId, cuentaProveedorId: cuentaId, tipo: "restaurante", nombre: "Terraza Mirador del Pino", contacto: "Persona de prueba", telefono: "3300000001", correo: null, ciudad: "Mazamitla", usuario: null, claveHash: null, estadoCuenta: "activa", cupoMaximo: 18, precioPorLugarCentavos: 0, intentosFallidos: 0, bloqueadoHasta: null, creadoEn: ahora() });
  for (const d of disponibilidadDemo("restaurante", 18, hoy)) a.tabla("disponibilidadReserva").push({ id: a.nuevoId(), proveedorId: lugarId, fecha: d.fecha, hora: d.hora, lugares: d.lugares, usados: 0 });
  for (const cabana of a.tabla("cabanas")) if (!cabana.proveedorReservaId) cabana.proveedorReservaId = cuentaId;
  a.guardar();
}

const proveedor = (a: A, id: unknown) => a.tabla("proveedoresReserva").find((p) => p.id === id);
const disp = (a: A, proveedorId: string, fecha: string, hora: string) => a.tabla("disponibilidadReserva").find((d) => d.proveedorId === proveedorId && iso(d.fecha) === fecha && d.hora === hora);
const solicitud = (a: A, id: string) => a.tabla("solicitudesReserva").find((s) => s.id === id);

function vista(s: Fila, p: Fila) {
  return { folio: String(s.folio), estado: String(s.estado), tipo: String(p.tipo), lugar: String(p.nombre), fecha: iso(s.fecha), hora: String(s.hora), personas: Number(s.personas), nombre: primerNombre(s.clienteNombre), montoCentavos: Number(s.montoCentavos ?? 0), metodoPago: String(s.metodoPago), codigo: (s.codigoQr as string) ?? null };
}

export async function lugaresDeProveedorLocal(a: A, cuentaId: string) {
  await asegurarDemoReservas(a);
  const cuenta = proveedor(a, cuentaId);
  if (!cuenta || cuenta.estadoCuenta !== "activa") throw new Error("Tu cuenta no está activa");
  const lugares = a.tabla("proveedoresReserva").filter((p) => p.id === cuentaId || p.cuentaProveedorId === cuentaId)
    .sort((x, y) => String(x.nombre).localeCompare(String(y.nombre)))
    .map((p) => ({ id: String(p.id), nombre: String(p.nombre), tipo: String(p.tipo), ciudad: p.ciudad ? String(p.ciudad) : null, principal: p.id === cuentaId, operable: true }));
  const ids = new Set(lugares.map((p) => p.id));
  const cabanas = a.tabla("cabanas").filter((c) => ids.has(String(c.proveedorReservaId)))
    .map((c) => ({ id: `cabana:${String(c.id)}`, nombre: String(c.nombre), tipo: "cabana", ciudad: null, principal: false, operable: false, detalle: `Cabaña para ${Number(c.capacidad)} personas` }));
  return [...lugares, ...cabanas];
}

// ───────── Proveedores y disponibilidad
export async function proveedoresPublicosLocal(a: A, tipo?: string) {
  await asegurarDemoReservas(a);
  return a.tabla("proveedoresReserva").filter((p) => p.estadoCuenta === "activa" && (!tipo || !R.esTipo(tipo) || p.tipo === tipo))
    .sort((x, y) => String(x.nombre).localeCompare(String(y.nombre)))
    .map((p) => ({ id: String(p.id), tipo: String(p.tipo), nombre: String(p.nombre), ciudad: (p.ciudad as string) ?? null, precioPorLugarCentavos: p.tipo === "caballeriza" ? Number(p.precioPorLugarCentavos ?? 0) : 0, cobra: p.tipo === "caballeriza", foto: (fotosDe(a, String(p.id))[0]?.archivo as string | undefined) ?? null }));
}

export async function disponibilidadLibreLocal(a: A, proveedorId: string, fecha: string) {
  await asegurarDemoReservas(a);
  const p = proveedor(a, proveedorId);
  if (!p || p.estadoCuenta !== "activa") throw new Error("Este lugar no recibe reservas por ahora");
  if (!/^\d{4}-\d{2}-\d{2}$/.test(fecha) || fecha < R.hoyMexico()) throw new Error("Elige una fecha de hoy en adelante");
  return a.tabla("disponibilidadReserva").filter((d) => d.proveedorId === proveedorId && iso(d.fecha) === fecha && R.lugaresLibres({ lugares: Number(d.lugares), usados: Number(d.usados) }) > 0)
    .sort((x, y) => String(x.hora).localeCompare(String(y.hora)))
    .map((d) => ({ hora: String(d.hora), libres: R.lugaresLibres({ lugares: Number(d.lugares), usados: Number(d.usados) }) }));
}

export async function disponibilidadDeProveedorLocal(a: A, proveedorId: string, desde: string) {
  await asegurarDemoReservas(a);
  const p = proveedor(a, proveedorId);
  if (!p || p.estadoCuenta !== "activa") throw new Error("Tu cuenta no está activa");
  const inicio = /^\d{4}-\d{2}-\d{2}$/.test(desde) ? desde : R.hoyMexico();
  const mapa = new Map(a.tabla("disponibilidadReserva").filter((d) => d.proveedorId === proveedorId).map((d) => [`${iso(d.fecha)}|${d.hora}`, d]));
  const dias = Array.from({ length: 7 }, (_, i) => {
    const fecha = R.sumarDias(inicio, i);
    return { fecha, horas: R.horasDe(String(p.tipo)).map((hora) => { const d = mapa.get(`${fecha}|${hora}`); return { hora, lugares: Number(d?.lugares ?? 0), usados: Number(d?.usados ?? 0) }; }) };
  });
  return { tipo: String(p.tipo), cupoMaximo: Number(p.cupoMaximo), desde: inicio, dias };
}

export async function guardarDisponibilidadLocal(a: A, proveedorId: string, cambios: { fecha: string; hora: string; lugares: number }[]) {
  await asegurarDemoReservas(a);
  const p = proveedor(a, proveedorId);
  if (!p || p.estadoCuenta !== "activa") throw new Error("Tu cuenta no está activa");
  if (!Array.isArray(cambios) || cambios.length === 0) throw new Error("No hay cambios que guardar");
  const hoy = R.hoyMexico();
  for (const c of cambios) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(c.fecha) || c.fecha < hoy) throw new Error("Solo puedes cambiar fechas de hoy en adelante");
    if (!R.horasDe(String(p.tipo)).includes(c.hora)) throw new Error("Hora no válida");
    const usados = Number(disp(a, proveedorId, c.fecha, c.hora)?.usados ?? 0);
    const error = R.ajusteValido(c.lugares, usados, Number(p.cupoMaximo));
    if (error) throw new Error(`${c.fecha} ${c.hora}: ${error}`);
  }
  for (const c of cambios) {
    const d = disp(a, proveedorId, c.fecha, c.hora);
    if (d) d.lugares = c.lugares;
    else a.tabla("disponibilidadReserva").push({ id: a.nuevoId(), proveedorId, fecha: c.fecha, hora: c.hora, lugares: c.lugares, usados: 0 });
  }
  a.guardar();
  return { ok: true as const, guardados: cambios.length };
}

// ───────── Solicitudes
export async function solicitarReservaLocal(a: A, entrada: Partial<R.DatosSolicitud> & R.DetallesSolicitud) {
  await asegurarDemoReservas(a);
  const p = proveedor(a, entrada.proveedorId);
  if (!p || p.estadoCuenta !== "activa") throw new Error("Este lugar no recibe reservas por ahora");
  const { error, datos } = R.validarSolicitud(entrada, String(p.tipo), R.hoyMexico());
  if (error) throw new Error(error);
  const detalle = R.validarDetalles(String(p.tipo), entrada);
  if (detalle) throw new Error(detalle);
  const d = disp(a, p.id as string, datos.fecha, datos.hora);
  if (!d || !R.cabeReserva({ lugares: Number(d.lugares), usados: Number(d.usados) }, datos.personas)) throw new Error("Ya no hay lugares en ese horario. Elige otra hora.");
  d.usados = Number(d.usados) + datos.personas;
  const s: Fila = {
    id: a.nuevoId(), folio: `MST-R${aleatorio(6)}`, proveedorId: p.id, clienteNombre: datos.clienteNombre, clienteTelefono: datos.clienteTelefono, clienteCorreo: datos.clienteCorreo ?? null,
    personas: datos.personas, fecha: datos.fecha, hora: datos.hora, estado: "solicitada", motivo: null, metodoPago: R.metodoPagoDe(String(p.tipo)),
    montoCentavos: R.montoDe(String(p.tipo), datos.personas, Number(p.precioPorLugarCentavos ?? 0)), cobradoEn: null, cargoRef: null, codigoQr: null, creadoEn: ahora(), respondidaEn: null, llegadaEn: null,
    ...R.detallesDe(entrada),
  };
  a.tabla("solicitudesReserva").unshift(s);
  bit(a, "solicitud", s.id as string, "solicitud recibida", undefined, { folio: s.folio, personas: datos.personas, fecha: datos.fecha, hora: datos.hora });
  a.guardar();
  return vista(s, p);
}

export async function reservaPorCodigoLocal(a: A, codigo: string) {
  await asegurarDemoReservas(a);
  const s = a.tabla("solicitudesReserva").find((x) => x.codigoQr === codigo);
  const p = s && proveedor(a, s.proveedorId);
  if (!s || !p) return null;
  return { ...vista(s, p), estado: String(s.estado), cobrado: !!s.cobradoEn };
}

export async function reservaPorFolioLocal(a: A, folio: string, telefono: string) {
  await asegurarDemoReservas(a);
  const tel = telefono.replace(/\D/g, "");
  const s = a.tabla("solicitudesReserva").find((x) => x.folio === folio.trim().toUpperCase());
  const p = s && proveedor(a, s.proveedorId);
  if (!s || !p || s.clienteTelefono !== tel) throw new Error("No encontramos esa reserva. Revisa el folio y tu WhatsApp.");
  return { ...vista(s, p), estado: String(s.estado), cobrado: !!s.cobradoEn };
}

export async function solicitudesDeProveedorLocal(a: A, proveedorId: string, f: { estado?: string; desde?: string; hasta?: string; fecha?: string }) {
  await asegurarDemoReservas(a);
  const estados = f.estado ? f.estado.split(",").filter((e) => (R.ESTADOS_SOLICITUD as readonly string[]).includes(e)) : [];
  return a.tabla("solicitudesReserva").filter((s) => s.proveedorId === proveedorId
    && (!estados.length || estados.includes(String(s.estado)))
    && (!f.fecha || iso(s.fecha) === f.fecha)
    && (!f.desde || iso(s.fecha) >= f.desde)
    && (!f.hasta || iso(s.fecha) <= f.hasta))
    .sort((x, y) => `${iso(x.fecha)}${x.hora}`.localeCompare(`${iso(y.fecha)}${y.hora}`))
    .map((s) => ({ id: s.id, folio: s.folio, fecha: iso(s.fecha), hora: s.hora, personas: s.personas, estado: s.estado, clienteNombre: s.clienteNombre, clienteTelefono: s.clienteTelefono, clienteCorreo: s.clienteCorreo ?? null, montoCentavos: s.montoCentavos, metodoPago: s.metodoPago, cobrado: !!s.cobradoEn, codigoQr: s.codigoQr ?? null, motivo: s.motivo ?? null, llegadaEn: s.llegadaEn ?? null, respondidaEn: s.respondidaEn ?? null, creadoEn: s.creadoEn }));
}

const MENSAJES: Record<R.AccionSolicitud, string> = {
  aceptar: "Reserva aceptada. Ya puedes reenviar el QR al cliente.",
  rechazar: "Solicitud rechazada. El cliente lo verá en su confirmación.",
  cancelar: "Reserva cancelada. Los lugares volvieron a estar disponibles.",
  llego: "Llegada registrada.",
  noLlego: "Marcada como no llegó.",
};

export async function responderSolicitudLocal(a: A, id: string, accion: string, motivo: string | undefined, proveedorId?: string) {
  const s = solicitud(a, id);
  if (!s || (proveedorId && s.proveedorId !== proveedorId)) throw new Error("No existe");
  if (!(accion in MENSAJES)) throw new Error("Acción no disponible");
  const ac = accion as R.AccionSolicitud;
  if ((ac === "rechazar" || ac === "cancelar") && !motivo?.trim()) throw new Error(ac === "rechazar" ? "Escribe el motivo del rechazo" : "Escribe el motivo de la cancelación");
  const antes = String(s.estado);
  const nuevo = R.transicion(antes, ac);
  const t = ahora();
  s.estado = nuevo;
  const despues: Record<string, unknown> = { estado: nuevo };
  if (ac === "aceptar") {
    s.respondidaEn = t; s.codigoQr = `MST-RES.${aleatorio(12)}`; despues.codigoQr = s.codigoQr;
    if (R.debeCobrarAlAceptar({ metodoPago: String(s.metodoPago), montoCentavos: Number(s.montoCentavos), cobradoEn: s.cobradoEn })) { s.cobradoEn = t; s.cargoRef = `PRUEBA-${s.folio}`; despues.cargoRef = s.cargoRef; }
  }
  if (ac === "rechazar" || ac === "cancelar") { s.respondidaEn = t; s.motivo = motivo!.trim(); }
  if (ac === "llego") s.llegadaEn = t;
  if (R.liberaLugares(nuevo)) {
    const d = disp(a, String(s.proveedorId), iso(s.fecha), String(s.hora));
    if (d) d.usados = Math.max(0, Number(d.usados) - Number(s.personas));
  }
  bit(a, "solicitud", id, ac, { estado: antes }, despues, motivo?.trim());
  a.guardar();
  return { mensaje: ac === "aceptar" && s.cargoRef ? `Reserva aceptada. Cargo de prueba de ${Number(s.montoCentavos) / 100} pesos a la cuenta del dueño.` : MENSAJES[ac], estado: nuevo, codigoQr: (s.codigoQr as string) ?? null };
}

export async function validarQrLocal(a: A, codigo: string, proveedorId?: string | null) {
  await asegurarDemoReservas(a);
  const s = a.tabla("solicitudesReserva").find((x) => x.codigoQr === codigo.trim());
  const hoy = R.hoyMexico();
  const base = s ? { proveedorId: String(s.proveedorId), estado: String(s.estado), fecha: iso(s.fecha), hora: String(s.hora), personas: Number(s.personas), clienteNombre: String(s.clienteNombre), llegadaEn: (s.llegadaEn as string) ?? null } : null;
  const r = R.evaluarQr(base, { proveedorId, hoy });
  if (!s || !r.valida) return r;
  s.estado = "llegó"; s.llegadaEn = ahora();
  bit(a, "solicitud", String(s.id), "llegada por QR", { estado: base!.estado }, { estado: "llegó" });
  a.guardar();
  return r;
}

// ───────── Cuentas de proveedor (entrada y panel)
export async function entrarProveedorLocal(a: A, usuarioEntrada: string, claveEntrada: string) {
  await asegurarDemoReservas(a);
  const usuario = normalizarUsuario(usuarioEntrada);
  const malo = new Error("Usuario o contraseña incorrectos");
  const p = usuario ? a.tabla("proveedoresReserva").find((x) => x.usuario === usuario) : undefined;
  if (!p) throw malo;
  const t = Date.now();
  const previo: Intentos = { fallos: Number(p.intentosFallidos ?? 0), hasta: p.bloqueadoHasta ? new Date(String(p.bloqueadoHasta)).getTime() : 0 };
  if (estaBloqueado(previo, t)) throw new Error(mensajeBloqueo(previo, t));
  const bien = !!p.claveHash && iguales(await hashSha(String(claveEntrada ?? ""), String(p.sal ?? "")), String(p.claveHash));
  if (!bien) {
    const n = conFallo(previo, t);
    p.intentosFallidos = n.fallos; p.bloqueadoHasta = n.hasta ? new Date(n.hasta).toISOString() : null; a.guardar();
    throw estaBloqueado(n, t) ? new Error(mensajeBloqueo(n, t)) : malo;
  }
  if (p.intentosFallidos || p.bloqueadoHasta) { p.intentosFallidos = 0; p.bloqueadoHasta = null; a.guardar(); }
  if (p.estadoCuenta === "pendiente") throw new Error("Tu cuenta está pendiente de activación");
  if (p.estadoCuenta === "suspendida") throw new Error("Tu cuenta está suspendida");
  return { id: String(p.id), nombre: String(p.nombre), tipo: String(p.tipo) };
}

const vistaPerfil = (p: Fila) => ({
  nombre: String(p.nombre), contacto: String(p.contacto), telefono: String(p.telefono), correo: p.correo ? String(p.correo) : null,
  ciudad: p.ciudad ? String(p.ciudad) : null, tipo: String(p.tipo), cupoMaximo: Number(p.cupoMaximo),
});
const textoPerfil = (v: unknown, etiqueta: string, maximo: number, obligatorio = true) => {
  const texto = String(v ?? "").trim().replace(/\s+/g, " ");
  if (obligatorio && !texto) throw new Error(`Escribe ${etiqueta}`);
  if (texto.length > maximo) throw new Error(`${etiqueta} es demasiado largo`);
  return texto;
};

export async function perfilProveedorLocal(a: A, id: string) {
  await asegurarDemoReservas(a);
  const p = proveedor(a, id);
  if (!p || p.estadoCuenta !== "activa") throw new Error("Tu cuenta no está activa");
  return vistaPerfil(p);
}

export async function guardarPerfilProveedorLocal(a: A, id: string, datos: Record<string, unknown>) {
  await asegurarDemoReservas(a);
  const p = proveedor(a, id);
  if (!p || p.estadoCuenta !== "activa") throw new Error("Tu cuenta no está activa");
  const nombre = textoPerfil(datos.nombre, "el nombre del negocio", 120);
  const contacto = textoPerfil(datos.contacto, "la persona de contacto", 100);
  const telefono = textoPerfil(datos.telefono, "un teléfono", 30).replace(/[^\d+()\-\s]/g, "");
  if (telefono.replace(/\D/g, "").length < 7) throw new Error("Escribe un teléfono válido");
  const correo = textoPerfil(datos.correo, "el correo", 160, false);
  if (correo && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(correo)) throw new Error("Escribe un correo válido");
  const ciudad = textoPerfil(datos.ciudad, "la ciudad", 80, false);
  const antes = vistaPerfil(p);
  Object.assign(p, { nombre, contacto, telefono, correo: correo || null, ciudad: ciudad || null });
  bit(a, "proveedorReserva", id, "actualizar perfil", antes, vistaPerfil(p));
  a.guardar();
  return vistaPerfil(p);
}

/** Alta desde el panel (vista previa): con usuario y contraseña la cuenta nace activa. Quita la contraseña en claro. */
export async function altaProveedorLocal(a: A, datos: Record<string, unknown>) {
  if (String(datos.cuentaProveedorId ?? "").trim()) {
    const cuenta = proveedor(a, datos.cuentaProveedorId);
    if (!cuenta) throw new Error("Elige una cuenta principal que exista");
    delete datos.usuario; delete datos.clave;
    datos.estadoCuenta = "activa";
    return;
  }
  const clave = datos.clave ? String(datos.clave) : "";
  delete datos.clave;
  const usuario = normalizarUsuario(datos.usuario);
  if (!usuario && !clave) { delete datos.usuario; datos.estadoCuenta = "pendiente"; return; }
  if (!usuario || !clave) throw new Error("Para dar acceso escribe el usuario y la contraseña");
  const error = validarUsuario(usuario) ?? validarClave(clave);
  if (error) throw new Error(error);
  if (a.tabla("proveedoresReserva").some((x) => x.usuario === usuario)) throw new Error("Ya existe un registro con ese valor (debe ser único).");
  if (!R.esTipo(datos.tipo)) throw new Error("El tipo debe ser restaurante o caballeriza");
  const sal = nuevaSal();
  Object.assign(datos, { usuario, sal, claveHash: await hashSha(clave, sal), estadoCuenta: "activa", intentosFallidos: 0, bloqueadoHasta: null });
}

export async function accionProveedorLocal(a: A, id: string, nombre: string, d: Record<string, string>): Promise<{ mensaje: string } | null> {
  if (!["activarCuenta", "suspender", "restablecerClave", "crearAcceso"].includes(nombre)) return null;
  const p = proveedor(a, id);
  if (!p) throw new Error("No existe");
  const reinicio = () => { p.intentosFallidos = 0; p.bloqueadoHasta = null; };
  if (nombre === "activarCuenta") {
    if (!p.usuario) throw new Error("Este proveedor no tiene usuario. Primero crea su acceso.");
    bit(a, "proveedorReserva", id, "activar cuenta", { estadoCuenta: p.estadoCuenta }, { estadoCuenta: "activa" });
    p.estadoCuenta = "activa"; reinicio();
    return { mensaje: "Cuenta activada: ya puede entrar al portal" };
  }
  if (nombre === "suspender") {
    bit(a, "proveedorReserva", id, "suspender cuenta", { estadoCuenta: p.estadoCuenta }, { estadoCuenta: "suspendida" });
    p.estadoCuenta = "suspendida";
    return { mensaje: "Cuenta suspendida" };
  }
  if (nombre === "restablecerClave") {
    if (!p.usuario) throw new Error("Este proveedor no tiene usuario. Primero crea su acceso.");
    const error = validarClave(d.clave ?? "");
    if (error) throw new Error(error);
    p.sal = nuevaSal(); p.claveHash = await hashSha(d.clave, String(p.sal)); reinicio();
    bit(a, "proveedorReserva", id, "restablecer contraseña");
    return { mensaje: "Contraseña cambiada. Avísale al proveedor por un medio seguro." };
  }
  if (p.cuentaProveedorId) throw new Error("Este lugar usa el acceso de su cuenta principal");
  if (p.usuario) throw new Error("Este proveedor ya tiene usuario");
  const usuario = normalizarUsuario(d.usuario);
  const error = validarUsuario(usuario) ?? validarClave(d.clave ?? "");
  if (error) throw new Error(error);
  if (a.tabla("proveedoresReserva").some((x) => x.usuario === usuario && x.id !== id)) throw new Error("Ese usuario ya está en uso. Elige otro.");
  const sal = nuevaSal();
  Object.assign(p, { usuario, sal, claveHash: await hashSha(d.clave, sal), estadoCuenta: "activa" }); reinicio();
  bit(a, "proveedorReserva", id, "crear acceso", undefined, { usuario });
  return { mensaje: `Acceso creado: usuario ${usuario}` };
}

// ───────── Fotos del lugar (vista previa; el proveedor las sube desde su portal)
const fotosDe = (a: A, id: string) => a.tabla("fotosProveedor").filter((f) => f.proveedorId === id).sort((x, y) => Number(x.orden) - Number(y.orden));
export async function fotosDeProveedorLocal(a: A, id: string) {
  await asegurarDemoReservas(a);
  if (!proveedor(a, id)) throw new Error("No existe");
  return fotosDe(a, id).map((f) => ({ id: String(f.id), archivo: String(f.archivo), alt: String(f.alt), orden: Number(f.orden) }));
}
export async function subirFotoProveedorLocal(a: A, id: string, datos: { archivo?: unknown; alt?: unknown }) {
  await asegurarDemoReservas(a);
  if (!proveedor(a, id)) throw new Error("No existe");
  const error = R.validarFoto(datos.archivo, datos.alt, fotosDe(a, id).length);
  if (error) throw new Error(error);
  const orden = fotosDe(a, id).reduce((m, f) => Math.max(m, Number(f.orden)), -1) + 1;
  const f = { id: a.nuevoId(), proveedorId: id, archivo: String(datos.archivo), alt: String(datos.alt).trim().slice(0, 160), orden };
  a.tabla("fotosProveedor").push(f);
  a.guardar();
  return { id: f.id, archivo: f.archivo, alt: f.alt, orden };
}
export async function quitarFotoProveedorLocal(a: A, id: string, fotoId: string) {
  const t = a.tabla("fotosProveedor");
  const i = t.findIndex((f) => f.id === fotoId && f.proveedorId === id);
  if (i < 0) throw new Error("No existe esa foto");
  t.splice(i, 1);
  a.guardar();
  return { ok: true as const };
}
export async function hacerPrincipalProveedorLocal(a: A, id: string, fotoId: string) {
  const todas = fotosDe(a, id);
  const pos = todas.findIndex((f) => f.id === fotoId);
  if (pos < 0) throw new Error("No existe esa foto");
  [todas[pos]!, ...todas.filter((_, i) => i !== pos)].forEach((f, i) => { f.orden = i; });
  a.guardar();
  return { ok: true as const };
}

/** Por lluvia: mueve las reservas aceptadas de un día a otro, con su mismo horario si hay lugar. Igual que el servidor. */
export async function reprogramarPorLluviaLocal(a: A, proveedorId: string, fechaOrigen: string, fechaNueva: string) {
  await asegurarDemoReservas(a);
  const error = R.validarReprogramacion(fechaOrigen, fechaNueva, R.hoyMexico());
  if (error) throw new Error(error);
  const aceptadas = a.tabla("solicitudesReserva").filter((s) => s.proveedorId === proveedorId && s.estado === "aceptada" && iso(s.fecha) === fechaOrigen);
  let movidas = 0;
  const sinLugar: string[] = [];
  for (const s of aceptadas) {
    const personas = Number(s.personas);
    const nuevo = disp(a, proveedorId, fechaNueva, String(s.hora));
    if (!nuevo || !R.cabeReserva({ lugares: Number(nuevo.lugares), usados: Number(nuevo.usados) }, personas)) { sinLugar.push(String(s.folio)); continue; }
    const viejo = disp(a, proveedorId, fechaOrigen, String(s.hora));
    nuevo.usados = Number(nuevo.usados) + personas;
    if (viejo) viejo.usados = Math.max(0, Number(viejo.usados) - personas);
    const original = s.reprogramadaDe ?? s.fecha;
    s.reprogramadaDe = original;
    s.fecha = fechaNueva;
    s.motivoCambio = "lluvia";
    bit(a, "solicitud", s.id as string, "reprogramada por lluvia", { fecha: fechaOrigen }, { fecha: fechaNueva }, "lluvia");
    movidas++;
  }
  a.guardar();
  return { movidas, sinLugar, total: aceptadas.length };
}
